import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { addDays, addMonths, addYears, isAfter, isBefore, startOfMonth } from 'date-fns';
import { BerlinDate, fromDatepicker, IN_BERLIN, isSameBerlinDay } from '@shared/berlin-time';
import { FisReportKind, MAX_PERIOD_YEARS, RUECKSTELLUNGEN_MIN_LAST_DAY } from './fis-report.const';

/**
 * Group validators of the report request form (controls `reports`, `start`,
 * `stop`). They mirror the backend rules of `POST /report/fis` (#5248) so
 * the user sees the problem before sending.
 */

interface Period {
  start: BerlinDate;
  /** Last day, inclusive. */
  stop: BerlinDate;
}

/** `periodOrder`: the last day lies before the first day. */
export const periodOrderValidator: ValidatorFn = (group) => {
  const period = readPeriod(group);
  if (!period) return null;
  return isBefore(period.stop, period.start) ? { periodOrder: true } : null;
};

/** `periodTooLong`: more than 10 years (exactly 10 years are allowed). */
export const periodMaxYearsValidator: ValidatorFn = (group) => {
  const period = readPeriod(group);
  if (!period) return null;
  const latestStop = addYears(period.start, MAX_PERIOD_YEARS, IN_BERLIN);
  return isAfter(exclusiveStop(period), latestStop) ? { periodTooLong: true } : null;
};

/**
 * `rueckstellungenMonth`: "Rückstellungen PBH" needs exactly one calendar
 * month; `rueckstellungenMinStart`: its key date (the last day) lies before
 * 31.12.2023.
 */
export const rueckstellungenMonthValidator: ValidatorFn = (group) => {
  const period = readPeriod(group);
  if (!period || !includesRueckstellungen(group)) return null;
  if (!isWholeCalendarMonth(period)) return { rueckstellungenMonth: true };
  return isBefore(period.stop, RUECKSTELLUNGEN_MIN_LAST_DAY)
    ? { rueckstellungenMinStart: true }
    : null;
};

export const FIS_REPORT_PERIOD_VALIDATORS: ValidatorFn[] = [
  periodOrderValidator,
  periodMaxYearsValidator,
  rueckstellungenMonthValidator,
];

/** The period of the group as Berlin days, or null while a day is missing. */
function readPeriod(group: AbstractControl): Period | null {
  const start = group.get('start')?.value;
  const stop = group.get('stop')?.value;
  if (!(start instanceof Date) || !(stop instanceof Date)) return null;
  return { start: fromDatepicker(start), stop: fromDatepicker(stop) };
}

function includesRueckstellungen(group: AbstractControl): boolean {
  const reports: unknown = group.get('reports')?.value;
  return Array.isArray(reports) && reports.includes(FisReportKind.RueckstellungenPbh);
}

/** The backend's exclusive `stop`: the day after the last day. */
function exclusiveStop(period: Period): Date {
  return addDays(period.stop, 1, IN_BERLIN);
}

function isWholeCalendarMonth(period: Period): boolean {
  const firstOfMonth = startOfMonth(period.start, IN_BERLIN);
  const firstOfNextMonth = addMonths(firstOfMonth, 1, IN_BERLIN);
  return (
    isSameBerlinDay(period.start, firstOfMonth) &&
    isSameBerlinDay(exclusiveStop(period), firstOfNextMonth)
  );
}

/** Reads a validation error of the group for the error line; null when valid. */
export function firstPeriodError(errors: ValidationErrors | null): string | null {
  if (!errors) return null;
  return Object.keys(errors)[0] ?? null;
}
